import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Settings,
  Palette,
  MessageSquare,
  User,
  Send,
  Bot,
  X,
  Plus,
  Trash2,
  GripVertical,
  BookOpen,
  Globe,
  Copy,
  Check,
  AlertTriangle,
  MessageCircle,
  Monitor,
  Maximize,
  BarChart3,
} from 'lucide-react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useBrand } from '../context/BrandContext';
import KnowledgePanel from '../components/chatbot/KnowledgePanel';
import ChatbotAnalyticsPanel from '../components/chatbot/ChatbotAnalyticsPanel';

const MODEL_OPTIONS = [
  { value: 'haiku', label: 'Claude Haiku', description: 'Fast & affordable' },
  { value: 'sonnet', label: 'Claude Sonnet', description: 'Balanced performance' },
  { value: 'opus', label: 'Claude Opus', description: 'Previous generation' },
  { value: 'opus-4-7', label: 'Claude Opus 4.7', description: 'Latest & most capable' },
];

const BUBBLE_STYLES = [
  { value: 'rounded', label: 'Rounded' },
  { value: 'square', label: 'Square' },
];

const TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'persona', label: 'Persona', icon: User },
  { id: 'knowledge', label: 'Knowledge', icon: BookOpen },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'welcome', label: 'Welcome', icon: MessageSquare },
  { id: 'deploy', label: 'Deploy', icon: Globe },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const EMBED_MODES = [
  {
    id: 'bubble',
    label: 'Floating Bubble',
    description: 'Chat bubble in the corner of the page',
    icon: MessageCircle,
  },
  {
    id: 'inline',
    label: 'Inline',
    description: 'Embedded directly in a page container',
    icon: Monitor,
  },
  {
    id: 'fullpage',
    label: 'Full Page',
    description: 'Takes over the entire viewport',
    icon: Maximize,
  },
];

const POSITION_OPTIONS = [
  { id: 'bottom-right', label: 'Bottom Right' },
  { id: 'bottom-left', label: 'Bottom Left' },
];

function getEmbedBaseUrl(brandConfig) {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:3000';
  }
  return `https://${brandConfig.domain}`;
}

function getApiBaseUrl(brandConfig) {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8000/api';
  }
  return `https://${brandConfig.apiDomain}/api`;
}

function generateEmbedSnippet(chatbotId, mode, position, brandConfig) {
  const scriptUrl = `${getEmbedBaseUrl(brandConfig)}/embed/chatbot-embed.js`;
  const apiUrl = getApiBaseUrl(brandConfig);

  if (mode === 'bubble') {
    return `<!-- Able Chatbot Widget -->
<script>
  window.AbleChatbotConfig = { apiUrl: "${apiUrl}" };
</script>
<script src="${scriptUrl}"></script>
<div data-able-chatbot="${chatbotId}" data-mode="bubble" data-position="${position}"></div>`;
  }

  if (mode === 'inline') {
    return `<!-- Able Chatbot Widget (Inline) -->
<script>
  window.AbleChatbotConfig = { apiUrl: "${apiUrl}" };
</script>
<script src="${scriptUrl}"></script>
<div id="my-chatbot" data-able-chatbot="${chatbotId}" data-mode="inline" style="width:400px;height:600px"></div>`;
  }

  if (mode === 'fullpage') {
    return `<!-- Able Chatbot Widget (Full Page) -->
<script>
  window.AbleChatbotConfig = { apiUrl: "${apiUrl}" };
</script>
<script src="${scriptUrl}"></script>
<div data-able-chatbot="${chatbotId}" data-mode="fullpage"></div>`;
  }

  return '';
}

function DeployPanel({ chatbot }) {
  const { brandConfig } = useBrand();
  const [embedMode, setEmbedMode] = useState('bubble');
  const [position, setPosition] = useState('bottom-right');
  const [copied, setCopied] = useState(false);

  const snippet = generateEmbedSnippet(chatbot.id, embedMode, position, brandConfig);

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isDraft = chatbot.status === 'draft';

  return (
    <div className="space-y-5">
      {/* Draft warning */}
      {isDraft && (
        <div className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Chatbot is in draft mode</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              Activate your chatbot in the General tab before embedding. Draft chatbots won't respond to visitors.
            </p>
          </div>
        </div>
      )}

      {/* Embed mode selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          Embed Mode
        </label>
        <div className="space-y-2">
          {EMBED_MODES.map((mode) => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => setEmbedMode(mode.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                  embedMode === mode.id
                    ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className={`p-1.5 rounded-md ${
                  embedMode === mode.id
                    ? 'bg-brand-100 dark:bg-brand-800/30 text-brand-600 dark:text-brand-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    embedMode === mode.id ? 'text-brand-700 dark:text-brand-400' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {mode.label}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{mode.description}</p>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  embedMode === mode.id
                    ? 'border-brand-600 dark:border-brand-400'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {embedMode === mode.id && (
                    <div className="w-2 h-2 rounded-full bg-brand-600 dark:bg-brand-400" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Position selector (only for bubble mode) */}
      {embedMode === 'bubble' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Position
          </label>
          <div className="flex gap-2">
            {POSITION_OPTIONS.map((pos) => (
              <button
                key={pos.id}
                onClick={() => setPosition(pos.id)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  position === pos.id
                    ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {pos.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Embed code */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Embed Code
          </label>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                Copy
              </>
            )}
          </button>
        </div>
        <div className="relative">
          <pre className="w-full p-3 text-xs font-mono bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all leading-relaxed">
            {snippet}
          </pre>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Paste this snippet into the HTML of any page where you want the chatbot to appear.
        </p>
      </div>

      {/* Preview link for inline/bubble */}
      <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Preview</p>
        <p className="text-xs text-gray-400">
          The live preview on the right shows how your chatbot will look. To test the real AI responses, activate the chatbot and use the embed code on an external page.
        </p>
      </div>
    </div>
  );
}

function ChatPreview({ chatbot }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const primaryColor = chatbot?.branding?.primary_color || '#2563eb';
  const bgColor = chatbot?.branding?.background_color || '#ffffff';
  const bubbleStyle = chatbot?.branding?.bubble_style || 'rounded';
  const avatarUrl = chatbot?.branding?.avatar_url;
  const welcomeMessage = chatbot?.welcome_message || 'Hi! How can I help you today?';
  const suggestedQuestions = chatbot?.suggested_questions || [];
  const borderRadius = bubbleStyle === 'square' ? '8px' : '20px';
  const messageBorderRadius = bubbleStyle === 'square' ? '6px' : '16px';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (text) => {
    const content = text || input.trim();
    if (!content) return;
    setMessages(prev => [...prev, { role: 'user', content }]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "This is a preview. Connect your chatbot to see real AI responses." },
      ]);
      setIsTyping(false);
    }, 1200);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Preview label */}
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Live Preview</p>
      </div>

      {/* Chat widget preview */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-800/50">
        <div
          className="w-full max-w-sm shadow-2xl flex flex-col overflow-hidden"
          style={{
            borderRadius,
            height: '540px',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {/* Chat header */}
          <div
            className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <Bot className="w-5 h-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{chatbot?.name || 'AI Assistant'}</p>
              <p className="text-xs text-white/70">Online</p>
            </div>
            <button className="ml-auto p-1 text-white/60 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages area */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ backgroundColor: bgColor }}
          >
            {/* Welcome message */}
            <div className="flex items-end gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Bot className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                )}
              </div>
              <div
                className="px-3.5 py-2.5 text-sm max-w-[80%]"
                style={{
                  borderRadius: messageBorderRadius,
                  backgroundColor: bgColor === '#ffffff' ? '#f3f4f6' : 'rgba(255,255,255,0.1)',
                  color: bgColor === '#ffffff' ? '#1f2937' : '#ffffff',
                }}
              >
                {welcomeMessage}
              </div>
            </div>

            {/* Suggested questions */}
            {messages.length === 0 && suggestedQuestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pl-9">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    className="px-3 py-1.5 text-xs border rounded-full transition-colors cursor-pointer hover:bg-gray-50"
                    style={{
                      borderColor: `${primaryColor}40`,
                      color: primaryColor,
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Chat messages */}
            {messages.map((msg, i) => (
              <div key={i} className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Bot className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                    )}
                  </div>
                )}
                <div
                  className="px-3.5 py-2.5 text-sm max-w-[80%]"
                  style={{
                    borderRadius: messageBorderRadius,
                    ...(msg.role === 'user'
                      ? { backgroundColor: primaryColor, color: '#ffffff' }
                      : {
                          backgroundColor: bgColor === '#ffffff' ? '#f3f4f6' : 'rgba(255,255,255,0.1)',
                          color: bgColor === '#ffffff' ? '#1f2937' : '#ffffff',
                        }),
                  }}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex items-end gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Bot className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                </div>
                <div
                  className="px-4 py-3 flex gap-1"
                  style={{
                    borderRadius: messageBorderRadius,
                    backgroundColor: bgColor === '#ffffff' ? '#f3f4f6' : 'rgba(255,255,255,0.1)',
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div
            className="flex-shrink-0 px-3 py-3 border-t"
            style={{
              backgroundColor: bgColor,
              borderColor: bgColor === '#ffffff' ? '#e5e7eb' : 'rgba(255,255,255,0.1)',
            }}
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
                className="flex-1 px-3.5 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{
                  borderColor: bgColor === '#ffffff' ? '#e5e7eb' : 'rgba(255,255,255,0.2)',
                  backgroundColor: bgColor === '#ffffff' ? '#ffffff' : 'rgba(255,255,255,0.05)',
                  color: bgColor === '#ffffff' ? '#1f2937' : '#ffffff',
                  focusRingColor: `${primaryColor}40`,
                }}
              />
              <button
                onClick={() => handleSend()}
                className="p-2 rounded-full text-white transition-all hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ChatbotBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [chatbot, setChatbot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef(null);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Fetch chatbot
  useEffect(() => {
    async function fetchChatbot() {
      try {
        const response = await apiRequest(`/chatbots/${id}`);
        if (response.ok) {
          const data = await response.json();
          setChatbot(data);
        } else {
          showError('Chatbot not found');
          navigate('/chatbots');
        }
      } catch (error) {
        console.error('Error fetching chatbot:', error);
        showError('Failed to load chatbot');
        navigate('/chatbots');
      } finally {
        setLoading(false);
      }
    }
    fetchChatbot();
  }, [id, navigate, showError]);

  // Auto-save with debounce
  const autoSave = useCallback(
    (updatedChatbot) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        try {
          setSaving(true);
          const { id: _, organization_id, created_by, created_at, ...updateData } = updatedChatbot;
          const response = await apiRequest(`/chatbots/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updateData),
          });
          if (!response.ok) throw new Error('Failed to save');
        } catch (error) {
          console.error('Auto-save failed:', error);
        } finally {
          setSaving(false);
        }
      }, 800);
    },
    [id]
  );

  const updateField = (field, value) => {
    setChatbot((prev) => {
      const updated = { ...prev, [field]: value };
      autoSave(updated);
      return updated;
    });
  };

  const updateBranding = (key, value) => {
    setChatbot((prev) => {
      const updated = {
        ...prev,
        branding: { ...prev.branding, [key]: value },
      };
      autoSave(updated);
      return updated;
    });
  };

  const addSuggestedQuestion = () => {
    const current = chatbot.suggested_questions || [];
    updateField('suggested_questions', [...current, '']);
  };

  const updateSuggestedQuestion = (index, value) => {
    const current = [...(chatbot.suggested_questions || [])];
    current[index] = value;
    updateField('suggested_questions', current);
  };

  const removeSuggestedQuestion = (index) => {
    const current = [...(chatbot.suggested_questions || [])];
    current.splice(index, 1);
    updateField('suggested_questions', current);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!chatbot) return null;

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Top bar */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link
            to="/chatbots"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${chatbot.branding?.primary_color || '#2563eb'}15` }}
            >
              <Bot className="w-4 h-4" style={{ color: chatbot.branding?.primary_color || '#2563eb' }} />
            </div>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">{chatbot.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-xs text-gray-400">Saving...</span>
          )}
          {!saving && chatbot && (
            <span className="text-xs text-gray-400">All changes saved</span>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Settings panel (left) */}
        <div className="w-[420px] flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex overflow-hidden">
          {/* Vertical nav */}
          <div className="w-[110px] flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col py-2 gap-0.5">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center gap-1.5 px-2 py-3 text-xs font-medium transition-colors rounded-lg mx-1 ${
                    activeTab === tab.id
                      ? 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:hover:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {activeTab === 'general' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    value={chatbot.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors"
                    placeholder="My AI Assistant"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Description
                  </label>
                  <textarea
                    value={chatbot.description || ''}
                    onChange={(e) => updateField('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors resize-none"
                    placeholder="A brief description of what this chatbot does..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Status
                  </label>
                  <div className="flex gap-2">
                    {['draft', 'active'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateField('status', status)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          chatbot.status === status
                            ? status === 'active'
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                              : 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                            : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'persona' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    System Prompt
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    Define how the chatbot behaves, its personality, and what it should and shouldn't do.
                  </p>
                  <textarea
                    value={chatbot.system_prompt || ''}
                    onChange={(e) => updateField('system_prompt', e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors resize-none font-mono"
                    placeholder={`You are a helpful assistant for our company.\n\nYou should:\n- Be friendly and professional\n- Answer questions about our products\n- Help users find what they need\n\nYou should not:\n- Make up information\n- Discuss competitors`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Model
                  </label>
                  <div className="space-y-2">
                    {MODEL_OPTIONS.map((model) => (
                      <button
                        key={model.value}
                        onClick={() => updateField('model', model.value)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-left transition-colors ${
                          chatbot.model === model.value
                            ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div>
                          <p className={`text-sm font-medium ${
                            chatbot.model === model.value ? 'text-brand-700 dark:text-brand-400' : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {model.label}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">{model.description}</p>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          chatbot.model === model.value
                            ? 'border-brand-600 dark:border-brand-400'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {chatbot.model === model.value && (
                            <div className="w-2 h-2 rounded-full bg-brand-600 dark:bg-brand-400" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'knowledge' && (
              <KnowledgePanel chatbotId={id} />
            )}

            {activeTab === 'appearance' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={chatbot.branding?.primary_color || '#2563eb'}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer overflow-hidden"
                      style={{ padding: '2px' }}
                    />
                    <input
                      type="text"
                      value={chatbot.branding?.primary_color || '#2563eb'}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors font-mono"
                    />
                  </div>
                  {/* Quick color presets */}
                  <div className="flex gap-1.5 mt-2">
                    {['#2563eb', '#7c3aed', '#db2777', '#059669', '#d97706', '#dc2626', '#0891b2', '#4f46e5'].map((color) => (
                      <button
                        key={color}
                        onClick={() => updateBranding('primary_color', color)}
                        className={`w-7 h-7 rounded-lg transition-all hover:scale-110 ${
                          chatbot.branding?.primary_color === color ? 'ring-2 ring-offset-2 ring-gray-300 dark:ring-offset-gray-900' : ''
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Background Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={chatbot.branding?.background_color || '#ffffff'}
                      onChange={(e) => updateBranding('background_color', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer overflow-hidden"
                      style={{ padding: '2px' }}
                    />
                    <input
                      type="text"
                      value={chatbot.branding?.background_color || '#ffffff'}
                      onChange={(e) => updateBranding('background_color', e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors font-mono"
                    />
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    {['#ffffff', '#f9fafb', '#f3f4f6', '#1f2937', '#111827', '#0f172a'].map((color) => (
                      <button
                        key={color}
                        onClick={() => updateBranding('background_color', color)}
                        className={`w-7 h-7 rounded-lg border transition-all hover:scale-110 ${
                          chatbot.branding?.background_color === color ? 'ring-2 ring-offset-2 ring-gray-300 dark:ring-offset-gray-900' : ''
                        }`}
                        style={{ backgroundColor: color, borderColor: color === '#ffffff' || color === '#f9fafb' ? '#e5e7eb' : 'transparent' }}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Bubble Style
                  </label>
                  <div className="flex gap-2">
                    {BUBBLE_STYLES.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => updateBranding('bubble_style', style.value)}
                        className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-lg border transition-colors ${
                          chatbot.branding?.bubble_style === style.value
                            ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                            : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div
                            className="w-5 h-3.5 bg-gray-300"
                            style={{ borderRadius: style.value === 'square' ? '2px' : '7px' }}
                          />
                          {style.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Avatar URL
                  </label>
                  <input
                    type="text"
                    value={chatbot.branding?.avatar_url || ''}
                    onChange={(e) => updateBranding('avatar_url', e.target.value || null)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors"
                    placeholder="https://example.com/avatar.png"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leave empty to use the default bot icon.
                  </p>
                </div>
              </>
            )}

            {activeTab === 'welcome' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Welcome Message
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    The first message visitors see when they open the chat.
                  </p>
                  <textarea
                    value={chatbot.welcome_message || ''}
                    onChange={(e) => updateField('welcome_message', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors resize-none"
                    placeholder="Hi! How can I help you today?"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Suggested Questions
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    Quick prompts shown to visitors to start the conversation.
                  </p>
                  <div className="space-y-2">
                    {(chatbot.suggested_questions || []).map((q, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="text-gray-300 dark:text-gray-600">
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <input
                          type="text"
                          value={q}
                          onChange={(e) => updateSuggestedQuestion(i, e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors"
                          placeholder="What can you help me with?"
                        />
                        <button
                          onClick={() => removeSuggestedQuestion(i)}
                          className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addSuggestedQuestion}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors w-full justify-center"
                    >
                      <Plus className="w-4 h-4" />
                      Add Question
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'deploy' && (
              <DeployPanel chatbot={chatbot} />
            )}

            {activeTab === 'analytics' && (
              <ChatbotAnalyticsPanel chatbotId={id} />
            )}
          </div>
        </div>

        {/* Preview panel (right) */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <ChatPreview chatbot={chatbot} />
        </div>
      </div>
    </div>
  );
}
